// E:\PORTALITH\frontend\src\components\modal\InspectionExportModal.jsx
import React, { useState, useEffect } from 'react';
import { api } from '../../contexts/authClient';
import { 
  X, Download, FileText, FileSpreadsheet, Loader, 
  Camera, CheckCircle, XCircle, AlertTriangle, Clock 
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const InspectionExportModal = ({ inspectionId, onClose }) => {
  const [inspection, setInspection] = useState(null);
  const [items, setItems] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchInspectionData();
  }, [inspectionId]);

  const fetchInspectionData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/inspections/${inspectionId}`);
      const data = response.data;
      setInspection(data);
      
      const inspectionItems = data.items || [];
      setItems(inspectionItems);
      
      const resultsMap = {};
      inspectionItems.forEach(item => {
        if (item.result) {
          resultsMap[item.id] = item.result;
        }
      });
      setResults(resultsMap);
    } catch (error) {
      console.error('Error fetching inspection:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pass': return <CheckCircle size={12} className="text-green-400" />;
      case 'fail': return <XCircle size={12} className="text-red-400" />;
      case 'warning': return <AlertTriangle size={12} className="text-yellow-400" />;
      case 'deferred': return <Clock size={12} className="text-orange-400" />;
      default: return null;
    }
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      // Sheet 1: Inspection Summary
      const summaryData = [{
        'Inspection #': inspection?.inspection_number,
        'Template': inspection?.template_name,
        'Zone': inspection?.zone_name || inspection?.zone_type,
        'Scheduled Date': inspection?.scheduled_date,
        'Performed Date': inspection?.performed_date,
        'Performed By': inspection?.performed_by_name,
        'Status': inspection?.status,
        'Overall Result': inspection?.overall_result?.toUpperCase(),
        'Findings Summary': inspection?.findings_summary,
        'NOTAM Issued': inspection?.notam_issued ? 'Yes' : 'No',
        'NOTAM Number': inspection?.notam_number || 'N/A'
      }];

      // Sheet 2: Checklist Results
      const checklistData = items.map(item => {
        const result = results[item.id] || {};
        return {
          'Order': item.item_order,
          'Code': item.item_code,
          'Item': item.item_label,
          'Instruction': item.item_instruction,
          'Result': result.result_status?.toUpperCase() || 'N/A',
          'Value': result.value_number || result.value_text || (result.value_boolean ? 'Yes' : 'No') || '—',
          'Unit': item.value_unit || '—',
          'Critical': item.is_critical ? 'Yes' : 'No',
          'Comment': result.comment || '—',
          'Photos': result.photo_urls?.length || 0,
          'Requires Follow-up': result.requires_followup ? 'Yes' : 'No'
        };
      });

      // Sheet 3: Statistics
      const totalItems = items.length;
      const completedItems = Object.values(results).filter(r => r.result_status).length;
      const passCount = Object.values(results).filter(r => r.result_status === 'pass').length;
      const failCount = Object.values(results).filter(r => r.result_status === 'fail').length;
      const warningCount = Object.values(results).filter(r => r.result_status === 'warning').length;
      
      const statsData = [{
        'Total Items': totalItems,
        'Completed': completedItems,
        'Pass': passCount,
        'Fail': failCount,
        'Warning': warningCount,
        'Pass Rate': totalItems > 0 ? ((passCount / totalItems) * 100).toFixed(1) + '%' : '0%'
      }];

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.json_to_sheet(summaryData);
      const ws2 = XLSX.utils.json_to_sheet(checklistData);
      const ws3 = XLSX.utils.json_to_sheet(statsData);
      
      XLSX.utils.book_append_sheet(wb, ws1, 'Inspection Summary');
      XLSX.utils.book_append_sheet(wb, ws2, 'Checklist Results');
      XLSX.utils.book_append_sheet(wb, ws3, 'Statistics');
      
      XLSX.writeFile(wb, `inspection_${inspection?.inspection_number}_${new Date().toISOString().slice(0,19)}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF();
      const companyName = 'AeroTrick FBO';
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.setTextColor(30, 64, 175);
      doc.text('Inspection Report', 14, yPos);
      yPos += 10;

      // Inspection Info
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
      yPos += 10;

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Inspection #: ${inspection?.inspection_number}`, 14, yPos);
      yPos += 7;
      doc.text(`Template: ${inspection?.template_name}`, 14, yPos);
      yPos += 7;
      doc.text(`Zone: ${inspection?.zone_name || inspection?.zone_type || '—'}`, 14, yPos);
      yPos += 7;
      doc.text(`Date: ${inspection?.scheduled_date}`, 14, yPos);
      yPos += 7;
      doc.text(`Performed by: ${inspection?.performed_by_name || 'Not performed'}`, 14, yPos);
      yPos += 7;
      doc.text(`Status: ${inspection?.status}`, 14, yPos);
      yPos += 7;
      doc.text(`Overall Result: ${inspection?.overall_result?.toUpperCase() || 'N/A'}`, 14, yPos);
      yPos += 15;

      // Checklist Table
      const tableData = items.map(item => {
        const result = results[item.id] || {};
        return [
          item.item_order,
          item.item_code,
          item.item_label?.substring(0, 30) || '',
          result.result_status?.toUpperCase() || 'N/A',
          result.value_number || result.value_text || (result.value_boolean ? 'Yes' : 'No') || '—',
          result.comment?.substring(0, 30) || '—'
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['#', 'Code', 'Item', 'Result', 'Value', 'Comment']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14, right: 14 }
      });

      yPos = doc.lastAutoTable?.finalY + 10;

      // Findings Summary
      if (inspection?.findings_summary) {
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('Findings Summary:', 14, yPos);
        yPos += 7;
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        const lines = doc.splitTextToSize(inspection.findings_summary, 180);
        doc.text(lines, 14, yPos);
        yPos += lines.length * 7 + 10;
      }

      // Photos section
      const photos = [];
      items.forEach(item => {
        const result = results[item.id];
        if (result?.photo_urls?.length > 0) {
          result.photo_urls.forEach(url => {
            photos.push({ item: item.item_label, url });
          });
        }
      });

      if (photos.length > 0) {
        doc.text('Attached Photos:', 14, yPos);
        yPos += 7;
        photos.forEach((photo, idx) => {
          doc.setFontSize(9);
          doc.text(`${idx + 1}. ${photo.item}`, 14, yPos);
          doc.setTextColor(59, 130, 246);
          doc.textWithLink(`${API_BASE}${photo.url}`, 14, yPos + 5, { url: `${API_BASE}${photo.url}` });
          doc.setTextColor(0, 0, 0);
          yPos += 12;
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(156, 163, 175);
        doc.text(
          `Generated by AeroTrick for ${companyName} · © 2026 AeroTrick. All rights reserved.`,
          14,
          doc.internal.pageSize.height - 10
        );
      }

      doc.save(`inspection_${inspection?.inspection_number}_${new Date().toISOString().slice(0,19)}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-700">
        
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold text-lg">
            Export Inspection Report
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-blue-400" size={32} />
            </div>
          ) : (
            <>
              {/* Inspection Summary */}
              <div className="bg-gray-700/30 rounded-lg p-4 mb-4">
                <h4 className="text-white font-medium mb-2">Inspection Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-400">Inspection #:</div>
                    <div className="text-white">{inspection?.inspection_number}</div>
                    
                    <div className="text-gray-400">Template:</div>
                    <div className="text-white">{inspection?.template_name}</div>
                    
                    <div className="text-gray-400">Zone:</div>
                    <div className="text-white">{inspection?.zone_name || inspection?.zone_type || '—'}</div>
                    
                    <div className="text-gray-400">Scheduled Date:</div>
                    <div className="text-white">{inspection?.scheduled_date}</div>
                    
                    <div className="text-gray-400">Assigned To:</div>
                    <div className="text-white">{inspection?.assigned_to_name || 'Unassigned'}</div>
                    
                    <div className="text-gray-400">Performed By:</div>
                    <div className="text-white">{inspection?.performed_by_name || 'Not performed'}</div>
                    
                    <div className="text-gray-400">Performed Date:</div>
                    <div className="text-white">{inspection?.performed_date || '—'}</div>
                    
                    <div className="text-gray-400">Status:</div>
                    <div className="text-white">{inspection?.status}</div>
                    
                    <div className="text-gray-400">Overall Result:</div>
                    <div className={`font-medium ${
                    inspection?.overall_result === 'pass' ? 'text-green-400' : 
                    inspection?.overall_result === 'fail' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                    {inspection?.overall_result?.toUpperCase() || 'N/A'}
                    </div>
                </div>
                </div>
              {/* Checklist Preview */}
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">Checklist Items ({items.length})</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {items.map((item, idx) => {
                    const result = results[item.id];
                    return (
                      <div key={item.id} className="flex items-center justify-between text-sm p-2 bg-gray-800/50 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 w-6">{item.item_order}</span>
                          <span className="text-gray-300">{item.item_label}</span>
                          {item.is_critical && <span className="text-red-400 text-xs">CRITICAL</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {result?.result_status && (
                            <div className="flex items-center gap-1">
                              {getStatusIcon(result.result_status)}
                              <span className={`text-xs ${
                                result.result_status === 'pass' ? 'text-green-400' :
                                result.result_status === 'fail' ? 'text-red-400' :
                                result.result_status === 'warning' ? 'text-yellow-400' : 'text-gray-400'
                              }`}>
                                {result.result_status.toUpperCase()}
                              </span>
                            </div>
                          )}
                          {result?.photo_urls?.length > 0 && (
                            <Camera size={12} className="text-blue-400" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
          >
            Cancel
          </button>
          <button
            onClick={exportToExcel}
            disabled={exporting || loading}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            {exporting ? <Loader size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
            Excel
          </button>
          <button
            onClick={exportToPDF}
            disabled={exporting || loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            {exporting ? <Loader size={16} className="animate-spin" /> : <FileText size={16} />}
            PDF
          </button>
        </div>

      </div>
    </div>
  );
};

export default InspectionExportModal;